/**
 * Jupyter Container Service
 * Manages Docker-based JupyterLab containers (chuẩn hóa RTK Query)
 */

import { store } from '../store';
import apiSlice from '../store/slices/apiSlice';

class JupyterContainerService {
    /**
     * Start or get Jupyter Lab container for project
     * @param {string} projectId - Project ID
     * @param {Object} specs - Container specifications (optional)
     * @returns {Promise<Object>} Container access info
     */
    async startContainer(projectId, specs = {}) {
        try {
            return await store.dispatch(apiSlice.endpoints.jupyterStartContainer.initiate(projectId)).unwrap();
        } catch (error) {
            console.error('Error starting Jupyter container:', error);
            throw error;
        }
    }

    /**
     * Get container status
     * @param {string} projectId - Project ID
     * @returns {Promise<Object>} Container status
     */
    async getContainerStatus(projectId) {
        try {
            const result = await store.dispatch(apiSlice.endpoints.jupyterContainerStatus.initiate(projectId));
            if (result.isError && result.error) throw result.error;
            return result.data ?? result.payload;
        } catch (error) {
            console.error('Error getting container status:', error);
            throw error;
        }
    }

    /**
     * Stop container
     * @param {string} projectId - Project ID
     * @returns {Promise<Object>} Success response
     */
    async stopContainer(projectId) {
        try {
            return await store.dispatch(apiSlice.endpoints.jupyterStopContainer.initiate(projectId)).unwrap();
        } catch (error) {
            console.error('Error stopping container:', error);
            throw error;
        }
    }

    /**
     * Restart container
     * @param {string} projectId - Project ID
     * @returns {Promise<Object>} Success response
     */
    async restartContainer(projectId) {
        try {
            return await store.dispatch(apiSlice.endpoints.jupyterRestartContainer.initiate(projectId)).unwrap();
        } catch (error) {
            console.error('Error restarting container:', error);
            throw error;
        }
    }

    /**
     * Get container logs
     * @param {string} projectId - Project ID
     * @param {number} tail - Number of lines to tail
     * @returns {Promise<Object>} Container logs
     */
    async getContainerLogs(projectId, tail = 100) {
        try {
            const result = await store.dispatch(apiSlice.endpoints.jupyterContainerLogs.initiate({ projectId, tail }));
            if (result.isError && result.error) throw result.error;
            return result.data;
        } catch (error) {
            console.error('Error getting container logs:', error);
            throw error;
        }
    }

    /**
     * Remove container (dangerous operation)
     * @param {string} projectId - Project ID
     * @param {boolean} force - Force removal
     * @returns {Promise<Object>} Success response
     */
    async removeContainer(projectId, force = false) {
        try {
            return await store.dispatch(apiSlice.endpoints.jupyterRemoveContainer.initiate({ projectId, force })).unwrap();
        } catch (error) {
            console.error('Error removing container:', error);
            throw error;
        }
    }

    /**
     * List all containers user has access to
     * @returns {Promise<Object>} List of containers
     */
    async listMyContainers() {
        try {
            const result = await store.dispatch(apiSlice.endpoints.jupyterMyContainers.initiate());
            if (result.isError && result.error) throw result.error;
            return result.data;
        } catch (error) {
            console.error('Error listing containers:', error);
            throw error;
        }
    }

    /**
     * Wait for container to be ready
     * @param {string} projectId - Project ID
     * @param {number} maxAttempts - Maximum polling attempts
     * @param {number} intervalMs - Polling interval in milliseconds
     * @returns {Promise<object>} Container status
     */
    async waitForContainerReady(projectId, maxAttempts = 30, intervalMs = 2000) {
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            try {
                const status = await this.getContainerStatus(projectId);

                // Check if container is running
                if (status.status === 'running') {
                    // Give it a bit more time to fully initialize
                    await new Promise((resolve) => setTimeout(resolve, 2000));
                    return status;
                }

                // Wait before next attempt
                await new Promise((resolve) => setTimeout(resolve, intervalMs));
            } catch (error) {
                console.warn(`Attempt ${attempt + 1}/${maxAttempts} - Container not ready yet`);
                await new Promise((resolve) => setTimeout(resolve, intervalMs));
            }
        }

        throw new Error('Container failed to start within timeout period');
    }

    /**
     * Open JupyterLab in new tab with token.
     * Chờ container status 'running' rồi mới mở tab.
     * @param {string} projectId - Project ID
     * @param {function} onProgress - Progress callback
     */
    async openInNewTab(projectId, onProgress = null) {
        const buildUrl = (info) => {
            const port = info?.port != null ? String(info.port) : null;
            const base = info?.url || (port ? `http://${typeof window !== 'undefined' ? window.location.hostname : 'localhost'}:${port}` : null);
            const token = info?.token;
            if (!base || !token) return null;
            return `${base.replace(/\/$/, '')}/lab?token=${token}`;
        };

        const waitUpThenOpen = async (url) => {
            if (!url) return;
            if (onProgress) onProgress('Đang chờ container sẵn sàng...');
            await this.waitForContainerReady(projectId, 40, 2000);
            if (onProgress) onProgress('Đang mở JupyterLab...');
            window.open(url, '_blank');
        };

        try {
            if (onProgress) onProgress('Đang kết nối...');
            let accessInfo = await this.startContainer(projectId);

            let fullUrl = buildUrl(accessInfo);
            if (fullUrl) {
                await waitUpThenOpen(fullUrl);
                return accessInfo;
            }

            // Chưa có url/port (Docker chưa kịp cập nhật) → đợi rồi lấy status để lấy port
            if (onProgress) onProgress('Đang lấy thông tin container...');
            await new Promise((r) => setTimeout(r, 2000));
            accessInfo = await this.startContainer(projectId);
            fullUrl = buildUrl(accessInfo);
            if (fullUrl) {
                await waitUpThenOpen(fullUrl);
                return accessInfo;
            }

            // Thử lấy port từ container status (backend có thể đã lưu port trong DB)
            if (onProgress) onProgress('Đang chờ container sẵn sàng...');
            try {
                await this.waitForContainerReady(projectId, 20, 2000);
                const status = await this.getContainerStatus(projectId);
                if (status?.port && accessInfo?.token) {
                    fullUrl = buildUrl({ ...accessInfo, port: status.port, url: status.url || null });
                }
            } catch (_) {
                accessInfo = await this.startContainer(projectId).catch(() => accessInfo);
                fullUrl = buildUrl(accessInfo);
            }
            if (fullUrl) {
                await waitUpThenOpen(fullUrl);
                return accessInfo;
            }

            throw new Error('Không lấy được địa chỉ JupyterLab. Vui lòng thử lại sau vài giây.');
        } catch (error) {
            console.error('Error opening JupyterLab:', error);
            throw error;
        }
    }

    /**
     * Get iframe URL for embedded JupyterLab
     * @param {string} projectId - Project ID
     * @returns {Promise<string>} Iframe URL with token
     */
    async getIframeUrl(projectId) {
        try {
            // Start container and get access info
            const accessInfo = await this.startContainer(projectId);

            // Build URL with token (dùng hostname hiện tại nếu backend không trả url)
            const host = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
            const baseUrl = accessInfo.url || (accessInfo.port ? `http://${host}:${accessInfo.port}` : null);
            if (!baseUrl || !accessInfo.token) throw new Error('Không lấy được địa chỉ JupyterLab.');
            const iframeUrl = `${baseUrl.replace(/\/$/, '')}/lab?token=${accessInfo.token}`;

            console.log('JupyterLab iframe URL:', iframeUrl);

            return {
                url: iframeUrl,
                token: accessInfo.token,
                accessToken: accessInfo.access_token,
                containerId: accessInfo.container_id
            };
        } catch (error) {
            console.error('Error getting iframe URL:', error);
            throw error;
        }
    }
}

export default new JupyterContainerService();
